import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryClient, withClientId } from "@/lib/queryClient";
import {
  fetchWorkOrders,
  fetchWorkOrderById,
  fetchWorkOrderTechnicians,
  fetchAssignableTechnicians,
  createWorkOrder,
  updateWorkOrder,
  deleteWorkOrder,
  changeWorkOrderStatus,
  fetchServiceSummary,
  fetchSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  fetchReferenceIndex,
  fetchReferenceManufacturer,
  fetchPlanSuggestion,
  applyPlan,
  fetchReminders,
  runReminderJob,
} from "./api";
import type {
  WorkOrder,
  WorkOrderWriteBody,
  ServiceSchedule,
  ServiceScheduleWriteBody,
  WorkOrderTransitionStatus,
} from "@/types/service";

// --- Query key factory ---

export const SERVICE_KEYS = {
  workOrders: ["work-orders"] as const,
  workOrder: (id: string) => ["work-orders", id] as const,
  workOrdersFiltered: (filters: Record<string, unknown>) => ["work-orders", { filters }] as const,
  technicians: ["work-orders", "technicians"] as const,
  assignableTechnicians: ["work-orders", "assignable-technicians"] as const,
  summary: ["service", "summary"] as const,
  schedules: ["service", "schedules"] as const,
  reference: ["service", "reference"] as const,
  referenceManufacturer: (key: string) => ["service", "reference", key] as const,
  planSuggestion: (boatId: string) => ["service", "plan-suggestion", boatId] as const,
  reminders: ["reminders"] as const,
};

const invalidateAllService = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: SERVICE_KEYS.workOrders });
  qc.invalidateQueries({ queryKey: SERVICE_KEYS.summary });
  qc.invalidateQueries({ queryKey: SERVICE_KEYS.schedules });
  qc.invalidateQueries({ queryKey: SERVICE_KEYS.reminders });
  // Cross-module: inventory lists boat statuses
  qc.invalidateQueries({ queryKey: ["boats"] });
};

// --- Reads ---

export const useWorkOrders = (filters?: {
  q?: string;
  boat_id?: string;
  status?: string;
  priority?: string;
  open_only?: boolean;
  technician?: string;
  from?: string;
  to?: string;
}) => {
  return useQuery({
    queryKey: filters && Object.keys(filters).length > 0
      ? SERVICE_KEYS.workOrdersFiltered(filters)
      : SERVICE_KEYS.workOrders,
    queryFn: () => fetchWorkOrders(filters),
  });
};

export const useWorkOrderTechnicians = () => {
  return useQuery({
    queryKey: SERVICE_KEYS.technicians,
    queryFn: fetchWorkOrderTechnicians,
  });
};

export const useAssignableTechnicians = () => {
  return useQuery({
    queryKey: SERVICE_KEYS.assignableTechnicians,
    queryFn: fetchAssignableTechnicians,
  });
};

export const useWorkOrder = (workOrderId: string) => {
  return useQuery({
    queryKey: SERVICE_KEYS.workOrder(workOrderId),
    queryFn: () => fetchWorkOrderById(workOrderId),
    enabled: !!workOrderId,
  });
};

export const useServiceSummary = () => {
  return useQuery({
    queryKey: SERVICE_KEYS.summary,
    queryFn: fetchServiceSummary,
  });
};

export const useSchedules = (filters?: { q?: string; boat_id?: string }) => {
  return useQuery({
    queryKey: filters?.boat_id
      ? [...SERVICE_KEYS.schedules, { boat_id: filters.boat_id }]
      : SERVICE_KEYS.schedules,
    queryFn: () => fetchSchedules(filters),
  });
};

// --- Work Order Mutations ---

// Outbox keys: paused offline mutations are persisted with key + variables;
// replay goes through the setMutationDefaults registration below (see
// apps/frontend/AGENTS.md "offline outbox" for the contract).
export const WORK_ORDER_CREATE_KEY = ["outbox", "work-orders", "create"] as const;
export const WORK_ORDER_UPDATE_KEY = ["outbox", "work-orders", "update"] as const;
export const WORK_ORDER_DELETE_KEY = ["outbox", "work-orders", "delete"] as const;
export const WORK_ORDER_STATUS_KEY = ["outbox", "work-orders", "status"] as const;
export const SCHEDULE_CREATE_KEY = ["outbox", "service-schedules", "create"] as const;
export const SCHEDULE_UPDATE_KEY = ["outbox", "service-schedules", "update"] as const;
export const SCHEDULE_DELETE_KEY = ["outbox", "service-schedules", "delete"] as const;
export const PLAN_APPLY_KEY = ["outbox", "service-plans", "apply"] as const;
export const REMINDER_RUN_KEY = ["outbox", "service-reminders", "run"] as const;

// Replay functions must mirror the hook mutationFn exactly (same imported
// function) so an offline replay behaves identically to an online submit.
queryClient.setMutationDefaults(WORK_ORDER_CREATE_KEY, { mutationFn: createWorkOrder });
queryClient.setMutationDefaults(WORK_ORDER_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: Partial<WorkOrderWriteBody> }) =>
    updateWorkOrder(vars.id, vars.body),
});
queryClient.setMutationDefaults(WORK_ORDER_DELETE_KEY, { mutationFn: deleteWorkOrder });
queryClient.setMutationDefaults(WORK_ORDER_STATUS_KEY, {
  mutationFn: (vars: {
    id: string;
    status: WorkOrderTransitionStatus;
    engineHoursAtService?: number;
  }) => changeWorkOrderStatus(vars.id, vars.status, vars.engineHoursAtService),
});
queryClient.setMutationDefaults(SCHEDULE_CREATE_KEY, { mutationFn: createSchedule });
queryClient.setMutationDefaults(SCHEDULE_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: Partial<ServiceScheduleWriteBody> }) =>
    updateSchedule(vars.id, vars.body),
});
queryClient.setMutationDefaults(SCHEDULE_DELETE_KEY, { mutationFn: deleteSchedule });
queryClient.setMutationDefaults(PLAN_APPLY_KEY, { mutationFn: applyPlan });
queryClient.setMutationDefaults(REMINDER_RUN_KEY, { mutationFn: runReminderJob });

type CreateWorkOrderContext = {
  id: string;
  detailPrev: WorkOrder | undefined;
  listPrev: WorkOrder[] | undefined;
};

export const useCreateWorkOrder = () => {
  const qc = useQueryClient();
  const mutation = useMutation<
    WorkOrder,
    Error,
    WorkOrderWriteBody & { id: string },
    CreateWorkOrderContext
  >({
    mutationKey: WORK_ORDER_CREATE_KEY,
    mutationFn: createWorkOrder,
    // Optimistic insert (incl. while paused offline): the client-generated id
    // makes the record addressable immediately; rolled back only on a real
    // error — a paused mutation resumes later and refetches server truth.
    onMutate: (vars) => {
      const now = new Date().toISOString();
      const optimistic: WorkOrder = {
        id: vars.id,
        boat_id: vars.boat_id,
        boat_name: "",
        title: vars.title,
        description: vars.description ?? "",
        status: "pending",
        priority: vars.priority ?? "medium",
        assigned_technician: vars.assigned_technician ?? "",
        scheduled_date: vars.scheduled_date ?? null,
        due_date: vars.due_date ?? null,
        completed_at: null,
        schedule_id: vars.schedule_id ?? null,
        parts_cost: vars.parts_cost ?? 0,
        labor_hours: vars.labor_hours ?? 0,
        labor_rate: vars.labor_rate ?? 0,
        labor_cost: (vars.labor_hours ?? 0) * (vars.labor_rate ?? 0),
        created_at: now,
        updated_at: now,
      };
      const detailPrev = qc.getQueryData<WorkOrder>(SERVICE_KEYS.workOrder(vars.id));
      qc.setQueryData(SERVICE_KEYS.workOrder(vars.id), optimistic);
      const listPrev = qc.getQueryData<WorkOrder[]>(SERVICE_KEYS.workOrders);
      if (listPrev) {
        qc.setQueryData(SERVICE_KEYS.workOrders, [optimistic, ...listPrev]);
      }
      return { id: vars.id, detailPrev, listPrev };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.detailPrev === undefined) qc.removeQueries({ queryKey: SERVICE_KEYS.workOrder(ctx.id) });
      else qc.setQueryData(SERVICE_KEYS.workOrder(ctx.id), ctx.detailPrev);
      if (ctx.listPrev !== undefined) qc.setQueryData(SERVICE_KEYS.workOrders, ctx.listPrev);
    },
    onSuccess: () => invalidateAllService(qc),
  });
  return {
    ...mutation,
    mutate: (body: WorkOrderWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: WorkOrderWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  };
};

export const useUpdateWorkOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: WORK_ORDER_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: Partial<WorkOrderWriteBody> }) =>
      updateWorkOrder(vars.id, vars.body),
    onSuccess: (data) => {
      invalidateAllService(qc);
      qc.invalidateQueries({ queryKey: SERVICE_KEYS.workOrder(data.id) });
    },
  });
};

export const useDeleteWorkOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: WORK_ORDER_DELETE_KEY,
    mutationFn: (id: string) => deleteWorkOrder(id),
    onSuccess: () => invalidateAllService(qc),
  });
};

export const useChangeWorkOrderStatus = () => {
  const qc = useQueryClient();
  return useMutation<
    WorkOrder,
    Error,
    { id: string; status: WorkOrderTransitionStatus; engineHoursAtService?: number },
    { snapshots: [readonly unknown[], unknown][] }
  >({
    mutationKey: WORK_ORDER_STATUS_KEY,
    mutationFn: (vars: { id: string; status: WorkOrderTransitionStatus; engineHoursAtService?: number }) =>
      changeWorkOrderStatus(vars.id, vars.status, vars.engineHoursAtService),
    // Optimistic status flip across every cached work-order view (detail,
    // filtered lists, by-boat briefs) so the UI reflects the change even
    // while the mutation sits paused in the offline queue.
    onMutate: (vars) => {
      const snapshots = qc.getQueriesData({ queryKey: SERVICE_KEYS.workOrders });
      const patch = (item: unknown) =>
        item && typeof item === "object" && "id" in item && (item as { id: string }).id === vars.id
          ? { ...item, status: vars.status }
          : item;
      for (const [key, data] of snapshots) {
        if (Array.isArray(data)) {
          qc.setQueryData(key, data.map(patch));
        } else if (data && typeof data === "object" && (data as { id?: string }).id === vars.id) {
          qc.setQueryData(key, patch(data));
        }
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
    },
    onSuccess: (data) => {
      invalidateAllService(qc);
      qc.invalidateQueries({ queryKey: SERVICE_KEYS.workOrder(data.id) });
    },
  });
};

// --- Schedule Mutations ---

export const useCreateSchedule = () => {
  const qc = useQueryClient();
  const mutation = useMutation<ServiceSchedule, Error, ServiceScheduleWriteBody & { id: string }>({
    mutationKey: SCHEDULE_CREATE_KEY,
    mutationFn: createSchedule,
    onSuccess: () => invalidateAllService(qc),
  });
  return {
    ...mutation,
    mutate: (body: ServiceScheduleWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: ServiceScheduleWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  };
};

export const useUpdateSchedule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: SCHEDULE_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: Partial<ServiceScheduleWriteBody> }) =>
      updateSchedule(vars.id, vars.body),
    onSuccess: () => invalidateAllService(qc),
  });
};

export const useDeleteSchedule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: SCHEDULE_DELETE_KEY,
    mutationFn: (id: string) => deleteSchedule(id),
    onSuccess: () => invalidateAllService(qc),
  });
};

// --- Reference data + plans ---

export const useReferenceIndex = () => {
  return useQuery({
    queryKey: SERVICE_KEYS.reference,
    queryFn: fetchReferenceIndex,
    staleTime: Infinity, // version-controlled static dataset
  });
};

export const useReferenceManufacturer = (key: string, enabled = true) => {
  return useQuery({
    queryKey: SERVICE_KEYS.referenceManufacturer(key),
    queryFn: () => fetchReferenceManufacturer(key),
    enabled: enabled && !!key,
    staleTime: Infinity,
  });
};

export const usePlanSuggestion = (boatId: string) => {
  return useQuery({
    queryKey: SERVICE_KEYS.planSuggestion(boatId),
    queryFn: () => fetchPlanSuggestion(boatId),
    enabled: !!boatId,
  });
};

export const useApplyPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: PLAN_APPLY_KEY,
    mutationFn: (body: {
      boat_id: string;
      manufacturer_key?: string;
      item_keys?: string[];
      include_boat_level?: boolean;
    }) => applyPlan(body),
    onSuccess: (_data, vars) => {
      invalidateAllService(qc);
      qc.invalidateQueries({ queryKey: SERVICE_KEYS.planSuggestion(vars.boat_id) });
    },
  });
};

// --- Reminders ---

export const useReminders = (limit = 100) => {
  return useQuery({
    queryKey: [...SERVICE_KEYS.reminders, limit],
    queryFn: () => fetchReminders(limit),
  });
};

export const useRunReminderJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: REMINDER_RUN_KEY,
    mutationFn: runReminderJob,
    onSuccess: () => invalidateAllService(qc),
  });
};
